import {
  garpMinimumPositiveWeight,
  garpWeightDecimalPlaces,
  materialWeightDecimalPlaces,
  weightDecimalPlaces,
  weightStoragePrecision,
} from './constants';

export enum WeightUnit {
  Kilograms = 'Kilograms',
  Grams = 'Grams',
  Milligrams = 'Milligrams',
  Pounds = 'Pounds',
  Ounces = 'Ounces',
}

interface WeightUnitDefinition {
  unit: WeightUnit;
  symbol: string;
  labelKey: `weightConverter.units.${keyof typeof translations.weightConverter.units}`;
  kilograms: number;
}

export interface WeightRules {
  readonly decimalPlaces: number;
  readonly minimumPositiveWeight: number;
  readonly emptyValue: 0 | null;
}

export const materialWeightRules: WeightRules = {
  decimalPlaces: materialWeightDecimalPlaces,
  minimumPositiveWeight: 0,
  emptyValue: 0,
};

export const garpWeightRules: WeightRules = {
  decimalPlaces: garpWeightDecimalPlaces,
  minimumPositiveWeight: garpMinimumPositiveWeight,
  emptyValue: null,
};

export const weightUnits: Record<WeightUnit, WeightUnitDefinition> = {
  [WeightUnit.Kilograms]: {
    unit: WeightUnit.Kilograms,
    symbol: 'kg',
    labelKey: 'weightConverter.units.kilograms',
    kilograms: 1,
  },
  [WeightUnit.Grams]: {
    unit: WeightUnit.Grams,
    symbol: 'g',
    labelKey: 'weightConverter.units.grams',
    kilograms: 0.001,
  },
  [WeightUnit.Milligrams]: {
    unit: WeightUnit.Milligrams,
    symbol: 'mg',
    labelKey: 'weightConverter.units.milligrams',
    kilograms: 0.000001,
  },
  [WeightUnit.Pounds]: {
    unit: WeightUnit.Pounds,
    symbol: 'lb',
    labelKey: 'weightConverter.units.pounds',
    kilograms: 0.45359237,
  },
  [WeightUnit.Ounces]: {
    unit: WeightUnit.Ounces,
    symbol: 'oz',
    labelKey: 'weightConverter.units.ounces',
    kilograms: 0.028349523125,
  },
};

export function convertWeight(value: number, from: WeightUnit, to: WeightUnit) {
  return (value * weightUnits[from].kilograms) / weightUnits[to].kilograms;
}

export function parseWeight(value: string): number | null {
  const trimmed = value.trim();
  if (!/^(?:\d+(?:[.,]\d*)?|[.,]\d+)$/.test(trimmed)) return null;

  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

export function resolveWeightUnit(value?: string | null) {
  return (
    Object.values(WeightUnit).find((unit) => unit === value) ??
    WeightUnit.Kilograms
  );
}

export function formatWeightInput(value: number) {
  return value.toLocaleString('en-US', {
    useGrouping: false,
    maximumFractionDigits: weightDecimalPlaces,
  });
}

export function roundWeightForStorage(
  value: number,
  decimalPlaces: number,
  minimumPositiveWeight = 0,
) {
  const scale = 10 ** decimalPlaces;
  if (value > 0 && value < minimumPositiveWeight) return minimumPositiveWeight;
  const scaled = value * scale;
  return Number.isInteger(scaled)
    ? value
    : Math.round(scaled + Number.EPSILON * scaled) / scale;
}
import type translations from '@texas/i18n/en/translation.json';

export function formatPreferredWeight(
  value: number | null | undefined,
  preferredUnit: string | null | undefined,
  locale?: string,
): string | null {
  const unit = resolveWeightUnit(preferredUnit);
  if (
    value == null ||
    !Number.isFinite(value) ||
    value < 0 ||
    unit === WeightUnit.Kilograms
  )
    return null;

  const converted = convertWeight(value, WeightUnit.Kilograms, unit);
  return `${converted.toLocaleString(locale, {
    maximumSignificantDigits: 5,
  })} ${weightUnits[unit].symbol}`;
}

export function isValidStoredWeight(
  value: number | null | undefined,
  decimalPlaces: number,
) {
  return (
    value == null ||
    (Number.isFinite(value) &&
      value >= 0 &&
      value < 10 ** (weightStoragePrecision - decimalPlaces))
  );
}

export function parseWeightForStorage(
  amount: string,
  unit: WeightUnit,
  decimalPlaces: number,
  minimumPositiveWeight = 0,
  emptyValue: 0 | null = null,
): number | null {
  if (amount.trim() === '') return emptyValue;
  const parsed = parseWeight(amount);
  if (parsed == null) return NaN;
  const kilograms = roundWeightForStorage(
    convertWeight(parsed, unit, WeightUnit.Kilograms),
    Math.min(decimalPlaces, weightDecimalPlaces),
    minimumPositiveWeight,
  );
  return isValidStoredWeight(kilograms, decimalPlaces) ? kilograms : NaN;
}
